import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import apiClient from "@/api/client";
import type { User } from "@/types";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { toastHelpers } from "@/lib/toast-helpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Menu, Settings } from "lucide-react";
import Cookies from "js-cookie";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const token = Cookies.get("pos_token");

    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Decode JWT token to get user information
      const decodedToken = jwtDecode<User & { exp: number; iat: number }>(
        token
      );

      // Check if token has expired
      const currentTime = new Date().getTime();
      const tokenExpTime = decodedToken.exp * 1000;

      if (currentTime >= tokenExpTime) {
        apiClient.clearAuth();
        toastHelpers.sessionExpired();
        setIsLoading(false);
        return;
      }

      // Extract user information from decoded token
      const userFromToken: User = {
        id: decodedToken.id,
        name: decodedToken.name,
        email: decodedToken.email,
        phone: decodedToken.phone || "",
        role: decodedToken.role,
        status: decodedToken.status,
        created_at: decodedToken.created_at,
        updated_at: decodedToken.updated_at,
      };

      setUser(userFromToken);
    } catch (error) {
      // console.error("Failed to decode JWT token:", error);
      apiClient.clearAuth();
      toastHelpers.sessionExpired();
    }

    setIsLoading(false);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t("AdminLayout.Loading")}</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!apiClient.isAuthenticated() || !user) {
    toastHelpers.sessionExpired();
    return <Navigate to="/login" />;
  }

  // Check admin role
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">
            {t("AdminLayout.AccessDenied.Title")}
          </h1>
          <p className="text-muted-foreground mb-4">
            {t("AdminLayout.AccessDenied.Message")}
          </p>
          <Navigate to="/" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <AdminSidebar
        user={user}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <main className="flex-1 overflow-y-auto flex flex-col">
        {/* Mobile header bar */}
        {isMobile && (
          <div className="lg:hidden p-4 border-b border-border bg-card">
            <div className="flex items-center justify-between">
              {/* Left side - Menu button and logo */}
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(true)}
                  className="h-10 w-10 p-0"
                >
                  <Menu className="h-5 w-5" />
                </Button>

                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                    <img src="/favicon.ico" alt="Logo" className="w-5 h-5" />
                  </div>
                  <div>
                    <h1 className="font-bold text-sm text-foreground">
                      {t("AdminLayout.MobileHeader.AppName")}
                    </h1>
                    <p className="text-xs text-muted-foreground">
                      {t("AdminLayout.MobileHeader.Subtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right side - User info */}
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="font-medium text-xs text-foreground">
                    {user.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("AdminLayout.MobileHeader.Role")}
                  </div>
                </div>
                <Badge className="bg-red-100 text-red-800 font-medium text-xs">
                  <Settings className="w-3 h-3 mr-1" />
                  {t("AdminLayout.MobileHeader.Badge")}
                </Badge>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
